import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, User, MessageSquare, Plus, Send, LogOut, AlertTriangle, Search, Calendar, MapPin, FileText, Trash2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Decode a JWT's `exp` claim (seconds) into a millisecond timestamp. Returns null if unreadable.
// This only reads the expiry for client-side UX timing — the server still enforces the real expiry.
const getTokenExpiry = (tok) => {
  try {
    const payload = JSON.parse(atob(tok.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
};

const App = () => {
  const SCHOOL_NAME = "Passaic Valley Regional High School";
  const SCHOOL_LOGO = "/pv-logo.png";
  const PRIMARY_COLOR = "rgb(45, 80, 22)";
  const PRIMARY_HOVER = "rgb(35, 65, 18)";

  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [view, setView] = useState('dashboard');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
const [newTicket, setNewTicket] = useState({
  title: '',
  department: 'technology',
  priority: 'medium',
  location: '',
  description: '',
  requested_for_user_id: '',
  staffSearchQuery: '',
  showStaffDropdown: false
});
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allStaffMembers, setAllStaffMembers] = useState([]);
  const [deletedTickets, setDeletedTickets] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [sessionWarning, setSessionWarning] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ticketDraft, setTicketDraft] = useState({});
  
  // State for comment popup on ticket cards
  const [showCommentsForTicket, setShowCommentsForTicket] = useState(null);
  const [cardComments, setCardComments] = useState([]);
  const [cardCommentsLoading, setCardCommentsLoading] = useState(false);
  
  // State for merge functionality
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeableTickets, setMergeableTickets] = useState([]);
  const [selectedMergeTickets, setSelectedMergeTickets] = useState([]);
  const [mergeSearchQuery, setMergeSearchQuery] = useState('');
  const [mergeLoading, setMergeLoading] = useState(false);
  
  // State for user management
  const [users, setUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: ''
  });
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(null);
  
  // Function to gather device information
const getDeviceInfo = () => {
  const userAgent = navigator.userAgent;
  
  // Detect OS
  let os = 'Unknown OS';
  if (userAgent.indexOf('Win') !== -1) {
    if (userAgent.indexOf('Windows NT 10.0') !== -1) os = 'Windows 10/11';
    else if (userAgent.indexOf('Windows NT 6.3') !== -1) os = 'Windows 8.1';
    else if (userAgent.indexOf('Windows NT 6.2') !== -1) os = 'Windows 8';
    else if (userAgent.indexOf('Windows NT 6.1') !== -1) os = 'Windows 7';
    else os = 'Windows';
  } else if (userAgent.indexOf('Mac') !== -1) os = 'MacOS';
  else if (userAgent.indexOf('Linux') !== -1) os = 'Linux';
  else if (userAgent.indexOf('Android') !== -1) os = 'Android';
  else if (userAgent.indexOf('iOS') !== -1) os = 'iOS';
  
  // Detect Browser
  let browser = 'Unknown Browser';
  let browserVersion = '';
  if (userAgent.indexOf('Edg') !== -1) {
    browser = 'Microsoft Edge';
    browserVersion = userAgent.match(/Edg\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Chrome') !== -1) {
    browser = 'Google Chrome';
    browserVersion = userAgent.match(/Chrome\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Firefox') !== -1) {
    browser = 'Mozilla Firefox';
    browserVersion = userAgent.match(/Firefox\/(\d+\.\d+)/)?.[1] || '';
  } else if (userAgent.indexOf('Safari') !== -1) {
    browser = 'Safari';
    browserVersion = userAgent.match(/Version\/(\d+\.\d+)/)?.[1] || '';
  }
  
  // Try to get computer name
  let hostname = 'Unknown';
  try {
    hostname = window.location.hostname || 'Unknown';
  } catch (e) {
    hostname = 'Unable to detect';
  }
  
  return {
    hostname,
    os,
    browser: `${browser} ${browserVersion}`,
    user_agent: userAgent,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    timestamp: new Date().toISOString()
  };
};

  const api = async (endpoint, options = {}) => {
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
    // A 401 from the login routes means "bad credentials" — let the real server
    // message through. A 401 from any *other* route means the session expired.
    const isLoginRequest =
      endpoint.startsWith('/auth/login') || endpoint.startsWith('/auth/ad-login');
    if (response.status === 401 && !isLoginRequest) {
      handleLogout();
      throw new Error('Your session has expired. Please sign in again.');
    }
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const isEmail = loginForm.email.includes('@');
      let data;
      if (isEmail) {
        data = await api('/auth/login', {
          method: 'POST',
          body: JSON.stringify({ email: loginForm.email, password: loginForm.password })
        });
      } else {
        data = await api('/auth/ad-login', {
          method: 'POST',
          body: JSON.stringify({ username: loginForm.email, password: loginForm.password })
        });
      }
      setToken(data.token);
      setUser(data.user);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
	  setView('dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

const handleLogout = () => {
  setToken(null);
  setUser(null);
  setError('');
  setSessionWarning(false);
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  setView('dashboard');
};

  // Proactive session-expiry handling driven off the JWT's own exp claim.
  // Warns ~2 minutes before expiry, then cleanly logs out at expiry — no manual
  // page refresh needed. Re-runs whenever the token changes (login / restore).
  useEffect(() => {
    if (!token) return;
    const expMs = getTokenExpiry(token);
    if (!expMs) return; // unreadable token — let the normal 401 path handle it

    const msUntilExpiry = expMs - Date.now();

    // Token already expired (e.g. a tab left open overnight) — log out now.
    if (msUntilExpiry <= 0) {
      handleLogout();
      setError('Your session has expired. Please sign in again.');
      return;
    }

    const warnLeadMs = 2 * 60 * 1000; // show the heads-up 2 minutes before expiry
    const warnTimer = setTimeout(
      () => setSessionWarning(true),
      Math.max(msUntilExpiry - warnLeadMs, 0)
    );
    const expireTimer = setTimeout(() => {
      handleLogout();
      setError('Your session has expired. Please sign in again.');
    }, msUntilExpiry);

    return () => {
      clearTimeout(warnTimer);
      clearTimeout(expireTimer);
    };
  }, [token]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser && token) setUser(JSON.parse(storedUser));
  }, [token]);
  
  // Track user activity
useEffect(() => {
  const resetTimer = () => setLastActivity(Date.now());
  
  window.addEventListener('mousemove', resetTimer);
  window.addEventListener('keypress', resetTimer);
  window.addEventListener('click', resetTimer);
  window.addEventListener('scroll', resetTimer);
  
  return () => {
    window.removeEventListener('mousemove', resetTimer);
    window.removeEventListener('keypress', resetTimer);
    window.removeEventListener('click', resetTimer);
    window.removeEventListener('scroll', resetTimer);
  };
}, []);

// Check for inactivity every minute - logout after 60 minutes
useEffect(() => {
  const interval = setInterval(() => {
    const inactiveTime = Date.now() - lastActivity;
    const sixtyMinutes = 60 * 60 * 1000; // 60 minutes in milliseconds
    
    if (token && inactiveTime > sixtyMinutes) {
      handleLogout();
      setError('You have been logged out due to inactivity. Please log in again.');
    }
  }, 60000); // Check every minute
  
  return () => clearInterval(interval);
}, [token, lastActivity]);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const data = await api(`/tickets?status=${filterStatus}&department=${filterDepartment}`);
      setTickets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

 const fetchMyTickets = async () => {
    setLoading(true);
    try {
      const data = await api(`/tickets?assigned=me&status=${filterStatus}&department=${filterDepartment}`);
      setTickets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchTeamMembers = async (dept) => {
    try {
      const data = await api(`/users/department/${dept}`);
      setTeamMembers(data);
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };
  
  const fetchAllStaff = async () => {
  try {
    const data = await api('/users/all-staff');
    setAllStaffMembers(data);
  } catch (err) {
    console.error('Error fetching staff members:', err);
  }
};

// Function to fetch comments for card popup
const fetchCardComments = async (ticketId) => {
  try {
    setCardCommentsLoading(true);
    const data = await api(`/tickets/${ticketId}/comments`);
    setCardComments(data);
  } catch (err) {
    console.error('Error fetching card comments:', err);
    setCardComments([]);
  } finally {
    setCardCommentsLoading(false);
  }
};

// Handle showing comments popup
const handleShowComments = (e, ticket) => {
  e.stopPropagation();
  setShowCommentsForTicket(ticket);
  fetchCardComments(ticket.id);
};

// Handle closing comments popup
const handleCloseComments = (e) => {
  if (e) e.stopPropagation();
  setShowCommentsForTicket(null);
  setCardComments([]);
};

useEffect(() => {
  if (token) {
    if (view === 'my-tickets') {
      fetchMyTickets();
    } else {
      fetchTickets();
    }
  }
}, [token, filterStatus, filterDepartment, view]);

// Handle direct ticket links from emails
useEffect(() => {
  if (!token) return;
  
  const urlParams = new URLSearchParams(window.location.search);
  const ticketId = urlParams.get('ticket');
  
  if (ticketId) {
    // Fetch and open the ticket
    const loadTicket = async () => {
      try {
        const ticket = await api(`/tickets/${ticketId}`);
        openTicketDetail(ticket);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      } catch (err) {
        setError('Could not load ticket. It may have been deleted or you may not have permission to view it.');
      }
    };
    
    loadTicket();
  }
}, [token]);

// Auto-refresh tickets every 60 seconds
useEffect(() => {
  if (!token || (view !== 'tickets' && view !== 'dashboard' && view !== 'my-tickets')) return;
  
  const interval = setInterval(() => {
    console.log('Auto-refreshing tickets...');
    if (view === 'my-tickets') {
      fetchMyTickets();
    } else {
      fetchTickets();
    }
  }, 60000); // 60 seconds
  
  return () => clearInterval(interval);
}, [token, view]);

const handleCreateTicket = async () => {
  if (!newTicket.title || !newTicket.location || !newTicket.description) {
    setError('Please fill in all required fields');
    return;
  }
  setLoading(true);
  try {
    const formData = new FormData();
    
    // Add all ticket fields
    formData.append('title', newTicket.title);
    formData.append('department', newTicket.department);
    formData.append('priority', newTicket.priority);
    formData.append('location', newTicket.location);
    formData.append('description', newTicket.description);
    
    if (newTicket.requested_for_user_id) {
      formData.append('requested_for_user_id', newTicket.requested_for_user_id);
    }
    
    // Add device info for technology tickets
    if (newTicket.department === 'technology') {
      const deviceInfo = getDeviceInfo();
      formData.append('device_info', JSON.stringify(deviceInfo));
    }
    
    // Add attachments
    ticketAttachments.forEach(file => {
      formData.append('attachments', file);
    });
    
    const response = await fetch(`${API_URL}/tickets`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) throw new Error('Failed to create ticket');
    
    setNewTicket({ 
      title: '', 
      department: 'technology', 
      priority: 'medium', 
      location: '', 
      description: '',
      requested_for_user_id: '',
      staffSearchQuery: '',
      showStaffDropdown: false
    });
    setTicketAttachments([]);
    setView('tickets');
    fetchTickets();
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

 
  const updateTicket = async (ticketId, updates) => {
  try {
    await api(`/tickets/${ticketId}`, { method: 'PUT', body: JSON.stringify(updates) });
    fetchTickets();
    if (selectedTicket?.id === ticketId) {
      const updated = await api(`/tickets/${ticketId}`);
      setSelectedTicket(updated);
    }
  } catch (err) {
    setError(err.message);
  }
};

const saveTicketChanges = async () => {
  if (!selectedTicket) return;
  
  // Require resolution when marking as resolved
  if (ticketDraft.status === 'resolved' && !ticketDraft.resolution?.trim()) {
    setError('Please enter a resolution before marking this ticket as resolved.');
    return;
  }
  
  setLoading(true);
  try {
    await api(`/tickets/${selectedTicket.id}`, { 
      method: 'PUT', 
      body: JSON.stringify(ticketDraft) 
    });
    
    // Refresh ticket data
    fetchTickets();
    const updated = await api(`/tickets/${selectedTicket.id}`);
    setSelectedTicket(updated);
    
    // Reset draft to match current
    setTicketDraft({
      status: updated.status,
      priority: updated.priority,
      assigned_to: updated.assigned_to || '',
      department: updated.department,
      resolution: updated.resolution || ''
    });
    
    setError(''); // Clear any errors
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  const fetchComments = async (ticketId) => {
    try {
      const data = await api(`/tickets/${ticketId}/comments`);
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
    }
  };
  
  const deleteTicket = async (ticketId) => {
  try {
    await api(`/tickets/${ticketId}`, { method: 'DELETE' });
    setShowDeleteConfirm(false);
    setView('tickets');
    fetchTickets();
  } catch (err) {
    setError(err.message);
  }
};

const deleteComment = async (commentId) => {
  if (!window.confirm('Are you sure you want to delete this comment?')) {
    return;
  }
  
  try {
    await api(`/comments/${commentId}`, { method: 'DELETE' });
    // Refresh comments
    fetchComments(selectedTicket.id);
  } catch (err) {
    setError(err.message);
  }
};

const fetchDeletedTickets = async () => {
  setLoading(true);
  try {
    const data = await api('/tickets/deleted/list');
    setDeletedTickets(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const restoreTicket = async (ticketId) => {
  try {
    await api(`/tickets/${ticketId}/restore`, { method: 'PUT' });
    fetchDeletedTickets();
    fetchTickets();
  } catch (err) {
    setError(err.message);
  }
};

// Fetch list of tickets that can be merged into the current ticket
const fetchMergeableTickets = async (ticketId) => {
  try {
    setMergeLoading(true);
    const data = await api(`/tickets/${ticketId}/mergeable`);
    setMergeableTickets(data);
  } catch (err) {
    console.error('Error fetching mergeable tickets:', err);
    setError(err.message);
    setMergeableTickets([]);
  } finally {
    setMergeLoading(false);
  }
};

// Handle opening merge modal
const handleOpenMergeModal = () => {
  setShowMergeModal(true);
  setSelectedMergeTickets([]);
  setMergeSearchQuery('');
  fetchMergeableTickets(selectedTicket.id);
};

// Handle closing merge modal
const handleCloseMergeModal = () => {
  setShowMergeModal(false);
  setSelectedMergeTickets([]);
  setMergeSearchQuery('');
  setMergeableTickets([]);
};

// Toggle ticket selection for merge
const toggleMergeTicketSelection = (ticketId) => {
  setSelectedMergeTickets(prev => {
    if (prev.includes(ticketId)) {
      return prev.filter(id => id !== ticketId);
    } else {
      return [...prev, ticketId];
    }
  });
};

// Handle merge tickets
const handleMergeTickets = async () => {
  if (selectedMergeTickets.length === 0) {
    setError('Please select at least one ticket to merge');
    return;
  }

  if (!window.confirm(`Are you sure you want to merge ${selectedMergeTickets.length} ticket(s) into this ticket? This action cannot be undone.`)) {
    return;
  }

  try {
    setMergeLoading(true);
    await api(`/tickets/${selectedTicket.id}/merge`, {
      method: 'POST',
      body: JSON.stringify({ sourceTicketIds: selectedMergeTickets })
    });
    
    // Refresh ticket data
    const updated = await api(`/tickets/${selectedTicket.id}`);
    setSelectedTicket(updated);
    fetchComments(selectedTicket.id);
    fetchTickets();
    
    // Close modal
    handleCloseMergeModal();
    
    setError(''); // Clear any errors
    // Could add a success message here if desired
  } catch (err) {
    setError(err.message);
  } finally {
    setMergeLoading(false);
  }
};

// ============ USER MANAGEMENT FUNCTIONS ============

const fetchUsers = async () => {
  try {
    setLoading(true);
    const data = await api('/users');
    setUsers(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const openCreateUserModal = () => {
  setEditingUser(null);
  setUserForm({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: ''
  });
  setShowUserModal(true);
};

const openEditUserModal = (user) => {
  setEditingUser(user);
  setUserForm({
    name: user.name,
    email: user.email,
    password: '', // Don't pre-fill password
    role: user.role,
    department: user.department || ''
  });
  setShowUserModal(true);
};

const closeUserModal = () => {
  setShowUserModal(false);
  setEditingUser(null);
  setUserForm({
    name: '',
    email: '',
    password: '',
    role: 'staff',
    department: ''
  });
};

const handleSaveUser = async () => {
  try {
    setLoading(true);
    
    if (!userForm.name || !userForm.email) {
      setError('Name and email are required');
      return;
    }
    
    if (!editingUser && !userForm.password) {
      setError('Password is required for new users');
      return;
    }

    if (editingUser) {
      // Update existing user
      const updateData = {
        name: userForm.name,
        email: userForm.email,
        role: userForm.role,
        department: userForm.department || null
      };
      
      // Only include password if it was changed
      if (userForm.password) {
        updateData.password = userForm.password;
      }
      
      await api(`/users/${editingUser.id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });
    } else {
      // Create new user
      await api('/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          name: userForm.name,
          email: userForm.email,
          password: userForm.password,
          role: userForm.role,
          department: userForm.department || null
        })
      });
    }
    
    closeUserModal();
    fetchUsers();
    setError('');
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const handleDeleteUser = async (userId) => {
  try {
    setLoading(true);
    await api(`/users/${userId}`, { method: 'DELETE' });
    setShowDeleteUserConfirm(null);
    fetchUsers();
    setError('');
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

const getRoleBadgeColor = (role) => {
  switch (role) {
    case 'admin': return 'bg-purple-100 text-purple-800';
    case 'tech': return 'bg-blue-100 text-blue-800';
    case 'tech-admin': return 'bg-blue-200 text-blue-900';
    case 'facilities': return 'bg-green-100 text-green-800';
    case 'facilities-admin': return 'bg-green-200 text-green-900';
    case 'custodian': return 'bg-yellow-100 text-yellow-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getRoleDisplayName = (role) => {
  switch (role) {
    case 'admin': return 'Admin';
    case 'tech': return 'Tech Team';
    case 'tech-admin': return 'Tech Admin';
    case 'facilities': return 'Facilities';
    case 'facilities-admin': return 'Facilities Admin';
    case 'custodian': return 'Custodian';
    case 'staff': return 'Staff';
    default: return role;
  }
};

const filteredUsers = users.filter(u => {
  const matchesSearch = u.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                        u.email.toLowerCase().includes(userSearchQuery.toLowerCase());
  const matchesRole = userRoleFilter === 'all' || u.role === userRoleFilter;
  return matchesSearch && matchesRole;
});

// ============ END USER MANAGEMENT FUNCTIONS ============

  const handleAddComment = async () => {
  if (!commentText.trim()) return;
  try {
    if (commentAttachments.length > 0) {
      // Use FormData for attachments
      const formData = new FormData();
      formData.append('text', commentText);
      commentAttachments.forEach(file => {
        formData.append('attachments', file);
      });
      
      const response = await fetch(`${API_URL}/tickets/${selectedTicket.id}/comments/with-attachments`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) throw new Error('Failed to add comment');
    } else {
      // No attachments, use regular API
      await api(`/tickets/${selectedTicket.id}/comments`, {
        method: 'POST',
        body: JSON.stringify({ text: commentText })
      });
    }
    
    setCommentText('');
    setCommentAttachments([]);
    fetchComments(selectedTicket.id);
  } catch (err) {
    setError(err.message);
  }
};

const openTicketDetail = async (ticket) => {
  setSelectedTicket(ticket);
  setView('detail');
  fetchComments(ticket.id);
  fetchTeamMembers(ticket.department);
  
  // Initialize draft with current values
  setTicketDraft({
    status: ticket.status,
    priority: ticket.priority,
    assigned_to: ticket.assigned_to || '',
    department: ticket.department,
    resolution: ticket.resolution || ''
  });
};

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-red-50 text-red-700 border-red-200';
      case 'in-progress': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'resolved': return 'bg-green-50 text-green-700 border-green-200';
      default: return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="w-4 h-4" />;
      case 'in-progress': return <Clock className="w-4 h-4" />;
      case 'resolved': return <CheckCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'low': return 'bg-gray-100 text-gray-800 border-gray-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
  const searchMatch = searchQuery === '' || 
    ticket.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(searchQuery.toLowerCase()));
  return searchMatch;
}).sort((a, b) => {
  if (sortBy === 'status') {
    // Define status priority: open=1, in-progress=2, resolved=3
    const statusOrder = { 'open': 1, 'in-progress': 2, 'resolved': 3 };
    return statusOrder[a.status] - statusOrder[b.status];
  }
  // Default: sort by date (newest first)
  return new Date(b.created_at) - new Date(a.created_at);
});

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    inProgress: tickets.filter(t => t.status === 'in-progress').length,
    resolved: tickets.filter(t => t.status === 'resolved').length,
    urgent: tickets.filter(t => t.priority === 'urgent' && t.status !== 'resolved').length,
    high: tickets.filter(t => t.priority === 'high' && t.status !== 'resolved').length
};

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
          <div className="text-center mb-8">
            <img src={SCHOOL_LOGO} alt={`${SCHOOL_NAME} Logo`} className="h-20 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">{SCHOOL_NAME}</h1>
            <p className="text-gray-600 mt-2">Help Desk Portal</p>
          </div>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                placeholder="username"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <input
                type="password"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                onKeyPress={(e) => e.key === 'Enter' && handleLogin(e)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
            <button
              onClick={handleLogin}
              disabled={loading}
              style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
              className="w-full text-white py-2 rounded-lg transition font-medium disabled:bg-gray-400"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </div>
        </div>
      </div>
    );
  }

return (
  <div className="min-h-screen bg-gray-50">
    <style>{`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `}</style>
      <div className="border-b border-gray-200 sticky top-0 z-50 shadow-sm" style={{backgroundColor: PRIMARY_COLOR}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
  <div className="flex items-center gap-4 cursor-pointer" onClick={() => { setView('dashboard'); setFilterStatus('all'); setFilterDepartment('all'); }}>
    <img src={SCHOOL_LOGO} alt={`${SCHOOL_NAME} Logo`} className="h-10 w-auto bg-white rounded p-1" />
    <div className="hidden md:block">
      <h1 className="text-lg font-bold text-white">{SCHOOL_NAME}</h1>
      <p className="text-xs text-gray-100">Help Desk Portal</p>
    </div>
  </div>
            <div className="flex items-center gap-6">
              <nav className="hidden md:flex gap-1">
                <button onClick={() => { setView('dashboard'); setFilterStatus('all'); setFilterDepartment('all'); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'dashboard' ? 'bg-white bg-opacity-20 text-white' : 'text-gray-100 hover:bg-white hover:bg-opacity-10'}`}>Dashboard</button>
                <button onClick={() => { setView('tickets'); setSortBy('status'); setFilterStatus('all'); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'tickets' ? 'bg-white bg-opacity-20 text-white' : 'text-gray-100 hover:bg-white hover:bg-opacity-10'}`}>Tickets</button>
                {(user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || 
                  user?.role === 'facilities' || user?.role === 'facilities-admin' ||
                  user?.department === 'technology' || user?.department === 'facilities') && user?.role !== 'custodian' && (
				  <>
				  <button onClick={() => { setView('my-tickets'); setFilterStatus('all'); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'my-tickets' ? 'bg-white bg-opacity-20 text-white' : 'text-gray-100 hover:bg-white hover:bg-opacity-10'}`}>My Tickets</button>
                  <button onClick={() => { setView('deleted'); fetchDeletedTickets(); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'deleted' ? 'bg-white bg-opacity-20 text-white' : 'text-gray-100 hover:bg-white hover:bg-opacity-10'}`}>Deleted</button>
                  </>
				)}
                {user?.role === 'admin' && (
                  <button onClick={() => { setView('users'); fetchUsers(); }} className={`px-4 py-2 rounded-lg text-sm font-medium transition ${view === 'users' ? 'bg-white bg-opacity-20 text-white' : 'text-gray-100 hover:bg-white hover:bg-opacity-10'}`}>Users</button>
                )}
              </nav>
			  <button 
  onClick={async () => {
    setIsRefreshing(true);
    
    if (view === 'my-tickets') {
      await fetchMyTickets();
    } else {
      await fetchTickets();
    }
    
    setTimeout(() => setIsRefreshing(false), 1000);
  }} 
  className="p-2 text-gray-100 hover:text-white transition" 
  title="Refresh tickets"
  disabled={isRefreshing}
>
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    style={{
      animation: isRefreshing ? 'spin 1s ease-in-out' : 'none'
    }}
  >
    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
  </svg>
</button>
              <button onClick={() => setView('create')} style={{color: PRIMARY_COLOR}} className="bg-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-100 transition flex items-center gap-2">
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">New Ticket</span>
              </button>
              <div className="flex items-center gap-3">
                <div className="hidden sm:block text-right">
                  <p className="text-sm font-medium text-white">{user?.name}</p>
                  <p className="text-xs text-gray-100">{user?.role}</p>
                </div>
                <button onClick={handleLogout} className="p-2 text-gray-100 hover:text-white transition"><LogOut className="w-5 h-5" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {sessionWarning && (
          <div className="bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 flex-shrink-0" />
            <span>Your session is about to expire. Please save any work — you'll need to sign in again shortly.</span>
            <button onClick={() => setSessionWarning(false)} className="ml-auto font-bold">×</button>
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
            <button onClick={() => setError('')} className="float-right font-bold">×</button>
          </div>
        )}

{view === 'dashboard' && (
  <div className="space-y-8">
    {/* Welcome Section */}
    <div className="flex items-center justify-between">
      <div>
        <h2 className="text-3xl font-bold text-gray-900">Welcome back, {user?.name?.split(' ')[0]}</h2>
        <p className="text-gray-600 mt-2">Here's what's happening with your help desk today</p>
      </div>
      <div className="text-right">
        <p className="text-sm text-gray-500">Last updated</p>
        <p className="text-sm font-medium text-gray-900">{new Date().toLocaleTimeString()}</p>
      </div>
    </div>

    {/* Enhanced Stat Cards - 4 Across */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {/* Total Tickets */}
      <div 
  onClick={() => { setView('tickets'); setSortBy('status'); setFilterStatus('all'); }}
        className="group relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(45, 80, 22, 0.05) 0%, rgba(45, 80, 22, 0.12) 100%)',
          border: '2px solid',
          borderColor: PRIMARY_COLOR
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white shadow-sm">
              <FileText className="w-6 h-6" style={{color: PRIMARY_COLOR}} />
            </div>
            <div className="text-xs font-semibold px-3 py-1 rounded-full bg-white text-gray-600">
              ALL TIME
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600 mb-1">Total Tickets</p>
          <p className="text-4xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </div>

      {/* Open Tickets */}
      <div 
  onClick={() => { setFilterStatus('open'); setView('tickets'); setSortBy('date'); }}
        className="group relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(239, 68, 68, 0.12) 100%)',
          border: '2px solid #ef4444'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white shadow-sm">
              <AlertCircle className="w-6 h-6 text-red-500" />
            </div>
            {stats.open > 0 && (
              <div className="flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-red-500 text-white animate-pulse">
                <span className="w-2 h-2 bg-white rounded-full"></span>
                ACTIVE
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-gray-600 mb-1">Open Tickets</p>
          <p className="text-4xl font-bold text-red-600">{stats.open}</p>
          <p className="text-xs text-gray-500 mt-2">Require attention</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </div>

      {/* In Progress */}
      <div 
  onClick={() => { setFilterStatus('in-progress'); setView('tickets'); setSortBy('date'); }}
        className="group relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.05) 0%, rgba(234, 179, 8, 0.12) 100%)',
          border: '2px solid #eab308'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white shadow-sm">
              <Clock className="w-6 h-6 text-yellow-600" />
            </div>
            {stats.inProgress > 0 && (
              <div className="text-xs font-semibold px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                WORKING
              </div>
            )}
          </div>
          <p className="text-sm font-medium text-gray-600 mb-1">In Progress</p>
          <p className="text-4xl font-bold text-yellow-600">{stats.inProgress}</p>
          <p className="text-xs text-gray-500 mt-2">Being resolved</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </div>

      {/* Resolved */}
      <div 
  onClick={() => { setFilterStatus('resolved'); setView('tickets'); setSortBy('date'); }} 
        className="group relative overflow-hidden rounded-2xl p-6 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(34, 197, 94, 0.12) 100%)',
          border: '2px solid #22c55e'
        }}
      >
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-white shadow-sm">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="text-xs font-semibold px-3 py-1 rounded-full bg-green-100 text-green-700">
              DONE
            </div>
          </div>
          <p className="text-sm font-medium text-gray-600 mb-1">Resolved</p>
          <p className="text-4xl font-bold text-green-600">{stats.resolved}</p>
          <p className="text-xs text-gray-500 mt-2">Successfully closed</p>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-transparent to-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </div>
    </div>

    {/* Priority Alert - SMALLER VERSION */}
    {(stats.urgent > 0 || stats.high > 0) && (
      <div className="relative overflow-hidden rounded-xl p-4 shadow-md" style={{
        background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
        border: '2px solid #ef4444'
      }}>
        <div className="absolute top-0 right-0 w-20 h-20 bg-red-200 rounded-full -mr-10 -mt-10 opacity-20"></div>
        <div className="relative flex items-center gap-3">
          <div className="p-2 rounded-lg bg-red-500 shadow-md">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-gray-900">Priority Tickets Need Attention</h3>
              <span className="px-2 py-0.5 rounded-full bg-red-500 text-white text-xs font-bold">
                {stats.urgent + stats.high}
              </span>
            </div>
            <p className="text-sm text-gray-700 mt-1">
              {stats.urgent > 0 && <span className="font-semibold text-red-600">{stats.urgent} urgent</span>}
              {stats.urgent > 0 && stats.high > 0 && <span> and </span>}
              {stats.high > 0 && <span className="font-semibold text-orange-600">{stats.high} high priority</span>}
              {' '}tickets require immediate attention.
            </p>
          </div>
          <button 
            onClick={() => setView('tickets')} 
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all font-semibold text-sm shadow-md"
          >
            View →
          </button>
        </div>
      </div>
    )}

    {/* Recent Activity - Enhanced */}
	<div className="bg-white rounded-2xl shadow-lg border-2 p-8 mt-10" style={{borderColor: PRIMARY_COLOR, borderWidth: '3px'}}>
  <div className="flex items-center justify-between mb-6">
    <div>
      <h3 className="text-2xl font-bold text-gray-900">Active Tickets</h3>
      <p className="text-sm text-gray-600 mt-1">Open and in-progress tickets requiring attention</p>
    </div>
        <button 
          onClick={() => setView('tickets')} 
          className="px-4 py-2 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all flex items-center gap-2"
          style={{color: PRIMARY_COLOR}}
        >
          View All
          <span>→</span>
        </button>
      </div>
      
      <div className="space-y-4">
        {tickets.filter(t => t.status !== 'resolved').slice(0, 5).length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-gray-200">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <FileText className="w-8 h-8 text-gray-400" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">No tickets yet</h4>
            <p className="text-gray-600 mb-6">Get started by creating your first ticket</p>
            <button 
              onClick={() => setView('create')} 
              className="px-6 py-3 text-white rounded-lg font-semibold hover:opacity-90 transition-all shadow-lg"
              style={{backgroundColor: PRIMARY_COLOR}}
            >
              Create First Ticket
            </button>
          </div>
        ) : (
          tickets.filter(t => t.status !== 'resolved').slice(0, 5).map((ticket, index) => (
            <div 
              key={ticket.id}
              onClick={() => openTicketDetail(ticket)} 
              className="group bg-white rounded-2xl p-5 shadow-sm border border-gray-100 hover:shadow-xl hover:border-gray-200 transition-all duration-300 cursor-pointer"
              style={{
                animation: `fadeIn 0.3s ease-out ${index * 0.1}s both`
              }}
            >
              <style>
                {`
                  @keyframes fadeIn {
                    from {
                      opacity: 0;
                      transform: translateY(10px);
                    }
                    to {
                      opacity: 1;
                      transform: translateY(0);
                    }
                  }
                `}
              </style>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-bold text-gray-900 group-hover:text-gray-700 transition-colors">{ticket.title}</h4>
                    {ticket.merged_into && (
                      <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300 whitespace-nowrap">
                        🔗 Merged into #{ticket.merged_into}
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-lg text-xs font-bold border-2 whitespace-nowrap ${getPriorityColor(ticket.priority)}`}>
                      {ticket.priority.toUpperCase()}
                    </span>
                  </div>
                  
                  {/* Description - truncated to 2 lines */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ticket.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="p-1.5 rounded-md bg-gray-100">
                        <MapPin className="w-3.5 h-3.5" />
                      </div>
                      <span className="font-medium truncate">{ticket.location}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="p-1.5 rounded-md bg-gray-100">
                        <User className="w-3.5 h-3.5" />
                      </div>
                      <span className="truncate">{ticket.submitted_by_name}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <div className="p-1.5 rounded-md bg-gray-100">
                        <Calendar className="w-3.5 h-3.5" />
                      </div>
                      <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      <span className="text-xs text-gray-500">Assigned:</span>
                      <span className="font-medium truncate">{ticket.assigned_to_name || 'Unassigned'}</span>
                    </div>
                    {/* Comment indicator */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => handleShowComments(e, ticket)}
                        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition text-xs font-medium"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>{ticket.comment_count || 0} Comments</span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className={`px-4 py-2 rounded-xl text-xs font-bold border-2 flex items-center gap-2 whitespace-nowrap shadow-sm ${getStatusColor(ticket.status)}`}>
                  {getStatusIcon(ticket.status)}
                  {ticket.status.replace('-', ' ').toUpperCase()}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  </div>
)}

        {view === 'create' && (
          <div className="max-w-3xl mx-auto">
            <div className="mb-6">
              <button onClick={() => setView('dashboard')} className="text-gray-600 hover:text-gray-900 font-medium text-sm mb-4">← Back</button>
              <h2 className="text-2xl font-bold text-gray-900">Create New Ticket</h2>
              <p className="text-gray-600 mt-1">Submit a request for assistance</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="space-y-6">
			  {/* Only show for tech/facilities team members - NOT custodians */}
{(user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || 
  user?.role === 'facilities' || user?.role === 'facilities-admin' ||
  user?.department === 'technology' || user?.department === 'facilities') && user?.role !== 'custodian' && (
  <div className="relative">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      Create Ticket For
    </label>
    <input
      type="text"
      value={newTicket.staffSearchQuery || ''}
      onChange={(e) => {
        setNewTicket({ 
          ...newTicket, 
          staffSearchQuery: e.target.value,
          showStaffDropdown: true 
        });
      }}
      onFocus={() => {
        if (allStaffMembers.length === 0) {
          fetchAllStaff();
        }
        setNewTicket({ ...newTicket, showStaffDropdown: true });
      }}
      onBlur={() => {
        // Delay to allow click on dropdown item
        setTimeout(() => {
          setNewTicket({ ...newTicket, showStaffDropdown: false });
        }, 300);
      }}
      placeholder="Search staff member or leave blank for yourself..."
      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
    
    {/* Dropdown results */}
    {newTicket.showStaffDropdown && newTicket.staffSearchQuery && (
      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {allStaffMembers
          .filter(staff => 
            staff.name.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase()) ||
            staff.email.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase())
          )
          .slice(0, 10)
          .map(staff => (
            <div
              key={staff.id}
              onMouseDown={() => {
                setNewTicket({
                  ...newTicket,
                  requested_for_user_id: staff.id,
                  staffSearchQuery: staff.name,
                  showStaffDropdown: false
                });
              }}
              className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
            >
              <div className="font-medium text-gray-900">{staff.name}</div>
              <div className="text-xs text-gray-500">{staff.email}</div>
            </div>
          ))}
        {allStaffMembers.filter(staff => 
          staff.name.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase()) ||
          staff.email.toLowerCase().includes(newTicket.staffSearchQuery.toLowerCase())
        ).length === 0 && (
          <div className="px-4 py-3 text-gray-500 text-sm">No staff members found</div>
        )}
      </div>
    )}
    
    {/* Show selected staff member */}
    {newTicket.requested_for_user_id && !newTicket.staffSearchQuery && (
      <div className="mt-2 text-sm text-gray-600">
        Creating ticket for: <span className="font-medium">
          {allStaffMembers.find(s => s.id === parseInt(newTicket.requested_for_user_id))?.name || 'Selected user'}
        </span>
        <button
          type="button"
          onClick={() => setNewTicket({ ...newTicket, requested_for_user_id: '', staffSearchQuery: '' })}
          className="ml-2 text-red-600 hover:text-red-800"
        >
          Clear
        </button>
      </div>
    )}
    
    <p className="text-xs text-gray-500 mt-1">
      Type to search for a staff member, or leave blank to create for yourself
    </p>
  </div>
)}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                    <input type="text" value={newTicket.location} onChange={(e) => setNewTicket({ ...newTicket, location: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Room number or area" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department *</label>
                    {user?.role === 'custodian' ? (
                      <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
                        Facilities
                      </div>
                    ) : (
                      <select value={newTicket.department} onChange={(e) => setNewTicket({ ...newTicket, department: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                        <option value="technology">Technology</option>
                        <option value="facilities">Facilities</option>
                      </select>
                    )}
                  </div>
                </div>
                {(user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || 
                  user?.role === 'facilities' || user?.role === 'facilities-admin' ||
                  user?.department === 'technology' || user?.department === 'facilities') && user?.role !== 'custodian' ? (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Priority *</label>
    <select value={newTicket.priority} onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
      <option value="low">Low</option>
      <option value="medium">Medium</option>
      <option value="high">High</option>
      <option value="urgent">Urgent</option>
    </select>
  </div>
) : (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
    <div className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-600">
      Medium (Default)
    </div>
    <p className="text-xs text-gray-500 mt-1">Priority will be set to Medium. {user?.role === 'custodian' ? 'Facilities' : 'Tech'} team will adjust if needed.</p>
  </div>
)}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Issue Title *</label>
                  <input type="text" value={newTicket.title} onChange={(e) => setNewTicket({ ...newTicket, title: e.target.value })} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Brief description of the issue" />
                </div>
				  <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attachments (Optional)</label>
                  <input 
                    type="file" 
                    multiple 
                    onChange={(e) => setTicketAttachments(Array.from(e.target.files))}
                    accept="image/*,.pdf,.doc,.docx,.txt"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {ticketAttachments.length > 0 && (
                    <div className="mt-2 text-sm text-gray-600">
                      {ticketAttachments.length} file(s) selected
                    </div>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Max 5 files, 10MB each</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Detailed Description *</label>
                  <textarea value={newTicket.description} onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })} rows="5" className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Provide as much detail as possible about the issue..." />
                </div>
                <div className="flex gap-3 pt-4">
                  <button onClick={handleCreateTicket} disabled={loading} style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}} className="px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 transition">{loading ? 'Submitting...' : 'Submit Ticket'}</button>
                  <button onClick={() => setView('dashboard')} className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {view === 'tickets' && (
          <div className="bg-white rounded-2xl shadow-lg border-2 p-8 mt-10" style={{borderColor: PRIMARY_COLOR, borderWidth: '3px'}}>
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">All Tickets</h2>
              <p className="text-gray-600 mt-1">Manage and track support requests</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tickets..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <select value={filterDepartment} onChange={(e) => setFilterDepartment(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="all">All Departments</option>
                    <option value="technology">Technology</option>
                    <option value="facilities">Facilities</option>
                  </select>
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                    <option value="all">All Status</option>
                    <option value="open">Open</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">Loading tickets...</div>
              ) : filteredTickets.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-gray-400" /></div>
                  <p className="text-gray-500 text-lg font-medium">No tickets found</p>
                  <p className="text-gray-400 mt-1">Try adjusting your filters or create a new ticket</p>
                  <button onClick={() => setView('create')} style={{backgroundColor: PRIMARY_COLOR}} className="mt-6 px-6 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium">Create New Ticket</button>
                </div>
              ) : (
                filteredTickets.map(ticket => (
                  <div 
                    key={ticket.id} 
                    onClick={() => openTicketDetail(ticket)} 
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition cursor-pointer"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{ticket.title}</h3>
                          {ticket.merged_into && (
                            <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300 whitespace-nowrap">
                              🔗 Merged into #{ticket.merged_into}
                            </span>
                          )}
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                        </div>
                        
                        {/* Description - truncated to 2 lines */}
                        <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ticket.description}</p>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2"><span className="text-gray-400">Dept:</span><span className="font-medium">{ticket.department === 'technology' ? 'Tech' : 'Facilities'}</span></div>
                          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span className="truncate">{ticket.location}</span></div>
                          <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /><span className="truncate">{ticket.submitted_by_name}</span></div>
                          <div className="flex items-center gap-2"><span className="text-gray-400">Assigned:</span><span className="font-medium truncate">{ticket.assigned_to_name || 'Unassigned'}</span></div>
                          {/* Comment indicator */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={(e) => handleShowComments(e, ticket)}
                              className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition text-xs font-medium"
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              <span>{ticket.comment_count || 0} Comments</span>
                            </button>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(ticket.created_at).toLocaleString()}</span>
                        </div>
                      </div>
                      <div className={`px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getStatusColor(ticket.status)}`}>
                        {getStatusIcon(ticket.status)}
                        {ticket.status.replace('-', ' ').toUpperCase()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
		
	{view === 'my-tickets' && (
   <div className="bg-white rounded-2xl shadow-lg border-2 p-8" style={{borderColor: PRIMARY_COLOR, borderWidth: '3px'}}>
    <div className="mb-6">
      <h2 className="text-2xl font-bold text-gray-900">My Assigned Tickets</h2>
      <p className="text-gray-600 mt-1">Tickets currently assigned to you</p>
    </div>
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search tickets..." className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>
        <div className="flex gap-3">
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); fetchMyTickets(); }} className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in-progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
        </div>
      </div>
    </div>
    <div className="space-y-3">
      {loading ? (
        <div className="text-center py-12">Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-gray-400" /></div>
          <p className="text-gray-500 text-lg font-medium">No tickets assigned to you</p>
          <p className="text-gray-400 mt-1">Tickets assigned to you will appear here</p>
        </div>
      ) : (
        filteredTickets.map(ticket => (
          <div 
            key={ticket.id} 
            onClick={() => openTicketDetail(ticket)} 
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition cursor-pointer"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{ticket.title}</h3>
                  {ticket.merged_into && (
                    <span className="px-2 py-1 rounded-lg text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-300 whitespace-nowrap">
                      🔗 Merged into #{ticket.merged_into}
                    </span>
                  )}
                  <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                </div>
                
                {/* Description - truncated to 2 lines */}
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{ticket.description}</p>
                
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm text-gray-600">
                  <div className="flex items-center gap-2"><span className="text-gray-400">Dept:</span><span className="font-medium">{ticket.department === 'technology' ? 'Tech' : 'Facilities'}</span></div>
                  <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span className="truncate">{ticket.location}</span></div>
                  <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /><span className="truncate">{ticket.submitted_by_name}</span></div>
                  <div className="flex items-center gap-2"><Calendar className="w-3 h-3 text-gray-400" /><span>{new Date(ticket.created_at).toLocaleDateString()}</span></div>
                  {/* Comment indicator */}
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => handleShowComments(e, ticket)}
                      className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 transition text-xs font-medium"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      <span>{ticket.comment_count || 0} Comments</span>
                    </button>
                  </div>
                </div>
              </div>
              <div className={`px-3 py-1.5 rounded-md text-xs font-medium border flex items-center gap-1.5 whitespace-nowrap ${getStatusColor(ticket.status)}`}>
                {getStatusIcon(ticket.status)}
                {ticket.status.replace('-', ' ').toUpperCase()}
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  </div>
)}
	
		{view === 'deleted' && (
          <div className="bg-white rounded-2xl shadow-lg border-2 p-8" style={{borderColor: PRIMARY_COLOR, borderWidth: '3px'}}>
            <div className="mb-6">
              <button onClick={() => setView('tickets')} className="text-gray-600 hover:text-gray-900 font-medium text-sm mb-4">← Back to Tickets</button>
              <h2 className="text-2xl font-bold text-gray-900">Deleted Tickets</h2>
              <p className="text-gray-600 mt-1">View and restore deleted tickets</p>
            </div>
            <div className="space-y-3">
              {loading ? (
                <div className="text-center py-12">Loading deleted tickets...</div>
              ) : deletedTickets.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
                  <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"><FileText className="w-8 h-8 text-gray-400" /></div>
                  <p className="text-gray-500 text-lg font-medium">No deleted tickets</p>
                  <p className="text-gray-400 mt-1">Deleted tickets will appear here</p>
                </div>
              ) : (
                deletedTickets.map(ticket => (
                  <div key={ticket.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-md transition">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">{ticket.title}</h3>
                          <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${getPriorityColor(ticket.priority)}`}>{ticket.priority.toUpperCase()}</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-2"><span className="text-gray-400">Dept:</span><span className="font-medium">{ticket.department === 'technology' ? 'Tech' : 'Facilities'}</span></div>
                          <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /><span>{ticket.location}</span></div>
                          <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /><span>{ticket.submitted_by_name}</span></div>
                          <div className="flex items-center gap-2"><span className="text-gray-400">Deleted:</span><span>{new Date(ticket.deleted_at).toLocaleDateString()}</span></div>
                        </div>
                      </div>
                      <button 
                        onClick={() => restoreTicket(ticket.id)}
                        style={{backgroundColor: PRIMARY_COLOR}}
                        className="px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium whitespace-nowrap"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ============ USERS MANAGEMENT VIEW ============ */}
        {view === 'users' && (
          <div className="bg-white rounded-2xl shadow-lg border-2 p-8 mt-10" style={{borderColor: PRIMARY_COLOR, borderWidth: '3px'}}>
            <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
                <p className="text-gray-600 mt-1">Create and manage user accounts</p>
              </div>
              <button 
                onClick={openCreateUserModal}
                style={{backgroundColor: PRIMARY_COLOR}}
                className="px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Add User
              </button>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search users by name or email..."
                      value={userSearchQuery}
                      onChange={(e) => setUserSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="tech">Tech Team</option>
                  <option value="tech-admin">Tech Admin</option>
                  <option value="facilities">Facilities</option>
                  <option value="facilities-admin">Facilities Admin</option>
                  <option value="custodian">Custodian</option>
                  <option value="staff">Staff</option>
                </select>
              </div>
            </div>

            {/* Users Count */}
            <div className="mb-4 text-sm text-gray-600">
              Showing {filteredUsers.length} of {users.length} users
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="text-center py-12">Loading users...</div>
              ) : filteredUsers.length === 0 ? (
                <div className="p-12 text-center">
                  <div className="bg-gray-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-lg font-medium">No users found</p>
                  <p className="text-gray-400 mt-1">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Department</th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredUsers.map(u => (
                        <tr key={u.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="bg-gray-200 w-10 h-10 rounded-full flex items-center justify-center">
                                <User className="w-5 h-5 text-gray-600" />
                              </div>
                              <div className="font-medium text-gray-900">{u.name}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">{u.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(u.role)}`}>
                              {getRoleDisplayName(u.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                            {u.department ? (u.department === 'technology' ? 'Technology' : 'Facilities') : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => openEditUserModal(u)}
                                className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition text-sm font-medium"
                              >
                                Edit
                              </button>
                              {u.id !== user?.id && (
                                <button
                                  onClick={() => setShowDeleteUserConfirm(u)}
                                  className="px-3 py-1.5 text-red-600 hover:bg-red-50 rounded-lg transition text-sm font-medium"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Create/Edit Modal */}
        {showUserModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-gray-900">
                    {editingUser ? 'Edit User' : 'Create New User'}
                  </h3>
                  <button onClick={closeUserModal} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    value={userForm.name}
                    onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="jsmith@pvrhs.org"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password {editingUser ? '(leave blank to keep current)' : '*'}
                  </label>
                  <input
                    type="password"
                    value={userForm.password}
                    onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder={editingUser ? '••••••••' : 'Enter password'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
                  <select
                    value={userForm.role}
                    onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="staff">Staff</option>
                    <option value="tech">Tech Team</option>
                    <option value="tech-admin">Tech Admin</option>
                    <option value="facilities">Facilities</option>
                    <option value="facilities-admin">Facilities Admin</option>
                    <option value="custodian">Custodian</option>
                    <option value="admin">Admin</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {userForm.role === 'staff' && 'Can only see and manage their own tickets'}
                    {userForm.role === 'tech' && 'Full access to technology department tickets'}
                    {userForm.role === 'tech-admin' && 'Tech team lead with admin privileges'}
                    {userForm.role === 'facilities' && 'Full access to facilities department tickets'}
                    {userForm.role === 'facilities-admin' && 'Facilities team lead with admin privileges'}
                    {userForm.role === 'custodian' && 'Can submit facilities tickets, only sees own tickets'}
                    {userForm.role === 'admin' && 'Full system access including user management'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <select
                    value={userForm.department}
                    onChange={(e) => setUserForm({...userForm, department: e.target.value})}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">None</option>
                    <option value="technology">Technology</option>
                    <option value="facilities">Facilities</option>
                  </select>
                </div>
              </div>
              <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
                <button
                  onClick={closeUserModal}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveUser}
                  disabled={loading}
                  style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
                  className="px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium"
                >
                  {loading ? 'Saving...' : (editingUser ? 'Update User' : 'Create User')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete User Confirmation Modal */}
        {showDeleteUserConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="bg-red-100 w-12 h-12 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Delete User</h3>
                  <p className="text-gray-600">This action cannot be undone.</p>
                </div>
              </div>
              <p className="text-gray-700 mb-6">
                Are you sure you want to delete <strong>{showDeleteUserConfirm.name}</strong>? 
                Any tickets they have submitted will be preserved but no longer associated with an active user.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteUserConfirm(null)}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(showDeleteUserConfirm.id)}
                  disabled={loading}
                  className="px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                >
                  {loading ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ============ END USERS MANAGEMENT VIEW ============ */}

        {view === 'detail' && selectedTicket && (
          <div className="max-w-5xl mx-auto">
            <div className="mb-6">
              <button onClick={() => setView('tickets')} className="text-gray-600 hover:text-gray-900 font-medium text-sm mb-4">← Back to Tickets</button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 mb-3">{selectedTicket.title}</h2>
                      <div className="flex flex-wrap items-center gap-3">
                        <span className={`px-3 py-1.5 rounded-md text-sm font-medium border ${getPriorityColor(selectedTicket.priority)}`}>{selectedTicket.priority.toUpperCase()}</span>
                        <span className={`px-3 py-1.5 rounded-md text-sm font-medium border flex items-center gap-1.5 ${getStatusColor(selectedTicket.status)}`}>
                          {getStatusIcon(selectedTicket.status)}
                          {selectedTicket.status.replace('-', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Description</h3>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedTicket.description}</p>
					</div>
                  {selectedTicket.resolution && (
                    <div className="border-t border-gray-200 pt-4 mt-4">
                      <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                        <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4" />
                          Resolution
                        </h3>
                        <p className="text-green-700 whitespace-pre-wrap">{selectedTicket.resolution}</p>
                      </div>
                    </div>
                  )}
                  {(() => {
  try {
    let attachments;
    
    if (Array.isArray(selectedTicket.attachments)) {
      attachments = selectedTicket.attachments;
    }
    else if (typeof selectedTicket.attachments === 'string' && selectedTicket.attachments.trim() !== '') {
      attachments = JSON.parse(selectedTicket.attachments);
    }
    else {
      return null;
    }
    
    if (attachments && attachments.length > 0) {
      return (
        <div className="border-t border-gray-200 pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Attachments</h3>
          <div className="space-y-2">
            {attachments.map((filename, index) => (
              <a 
                key={index}
                href={`${API_URL.replace('/api', '')}/uploads/${filename}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
              >
                <FileText className="w-4 h-4" />
                {filename.substring(filename.indexOf('-') + 1)}
              </a>
            ))}
          </div>
        </div>
      );
    }
  } catch (e) {
    console.warn('Error parsing ticket attachments:', e);
    return null;
  }
  return null;
})()}
                </div>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Activity & Comments</h3>
                  <div className="space-y-4 mb-6">
                    {comments.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No comments yet</p>
                    ) : (
                      comments.map(comment => (
                        <div key={comment.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="bg-gray-200 w-8 h-8 rounded-full flex items-center justify-center"><User className="w-4 h-4 text-gray-600" /></div>
                              <div>
                                <p className="font-medium text-gray-900">{comment.author_name}</p>
                                <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleString()}</span>
                              </div>
                            </div>
                            
                            {/* Delete button - only show if user can delete */}
                            {(user?.id === comment.author_id || 
                              user?.role === 'admin' || 
                              user?.role === 'tech-admin' || 
                              user?.role === 'facilities-admin') && (
                              <button
                                onClick={() => deleteComment(comment.id)}
                                className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50"
                                title="Delete comment"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <p className="text-gray-700 ml-10">{comment.text}</p>
						 {(() => {
  try {
    let attachments;
    
    if (Array.isArray(comment.attachments)) {
      attachments = comment.attachments;
    }
    else if (typeof comment.attachments === 'string' && comment.attachments.trim() !== '') {
      attachments = JSON.parse(comment.attachments);
    }
    else {
      return null;
    }
    
    if (attachments && attachments.length > 0) {
      return (
        <div className="ml-10 mt-2 space-y-1">
          {attachments.map((filename, index) => (
            <a 
              key={index}
              href={`${API_URL.replace('/api', '')}/uploads/${filename}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-blue-600 hover:text-blue-800 text-xs"
            >
              <FileText className="w-3 h-3" />
              {filename.substring(filename.indexOf('-') + 1)}
            </a>
          ))}
        </div>
      );
    }
  } catch (e) {
    console.warn('Error parsing comment attachments:', e);
    return null;
  }
  return null;
})()}
                        </div>
                      ))
                    )}
                  </div>
                  <div className="border-t border-gray-200 pt-4">
                    <textarea
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                      rows="3"
                    />
                    <div className="flex items-center gap-3">
                      <input 
                        type="file" 
                        multiple 
                        id="comment-attachments"
                        onChange={(e) => setCommentAttachments(Array.from(e.target.files))}
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        className="hidden"
                      />
                      <label 
                        htmlFor="comment-attachments"
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition cursor-pointer text-sm font-medium"
                      >
                        📎 Attach Files
                      </label>
                      {commentAttachments.length > 0 && (
                        <span className="text-sm text-gray-600">
                          {commentAttachments.length} file(s)
                        </span>
                      )}
                      <button 
                        onClick={handleAddComment}
                        style={{backgroundColor: PRIMARY_COLOR}} 
                        className="ml-auto px-6 py-2.5 text-white rounded-lg font-medium hover:opacity-90 transition flex items-center gap-2"
                      >
                        <Send className="w-4 h-4" />
                        Add Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">Ticket Details</h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Department</p>
                      <p className="font-medium text-gray-900">{selectedTicket.department === 'technology' ? 'Technology' : 'Facilities'}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Location</p>
                      <p className="font-medium text-gray-900 flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" />{selectedTicket.location}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Submitted by</p>
                      <p className="font-medium text-gray-900 flex items-center gap-2"><User className="w-4 h-4 text-gray-400" />{selectedTicket.submitted_by_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Created</p>
                      <p className="font-medium text-gray-900 flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" />{new Date(selectedTicket.created_at).toLocaleString()}</p>
                    </div>
                  </div>
                </div>
				{/* Device Information - Only show to tech team for tech tickets */}
                {selectedTicket.department === 'technology' && selectedTicket.device_info && 
                 (user?.role === 'admin' || user?.role === 'tech' || user?.role === 'tech-admin' || user?.department === 'technology') && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Device Information</h3>
                    <div className="space-y-3 text-sm">
                      {(() => {
                        try {
                          const deviceInfo = JSON.parse(selectedTicket.device_info);
                          return (
                            <>
                              {deviceInfo.hostname && (
                                <div>
                                  <p className="text-gray-500 mb-1">Computer Name</p>
                                  <p className="font-medium text-gray-900">{deviceInfo.hostname}</p>
                                </div>
                              )}
                              {deviceInfo.ip_address && (
                                <div>
                                  <p className="text-gray-500 mb-1">IP Address</p>
                                  <p className="font-medium text-gray-900">{deviceInfo.ip_address}</p>
                                </div>
                              )}
                              {deviceInfo.os && (
                                <div>
                                  <p className="text-gray-500 mb-1">Operating System</p>
                                  <p className="font-medium text-gray-900">{deviceInfo.os}</p>
                                </div>
                              )}
                              {deviceInfo.browser && (
                                <div>
                                  <p className="text-gray-500 mb-1">Browser</p>
                                  <p className="font-medium text-gray-900">{deviceInfo.browser}</p>
                                </div>
                              )}
                              {deviceInfo.screen_resolution && (
                                <div>
                                  <p className="text-gray-500 mb-1">Screen Resolution</p>
                                  <p className="font-medium text-gray-900">{deviceInfo.screen_resolution}</p>
                                </div>
                              )}
                            </>
                          );
                        } catch (e) {
                          return <p className="text-gray-500">Device information unavailable</p>;
                        }
                      })()}
                    </div>
                  </div>
                )}
                {(user?.role === 'admin' || 
                  user?.role === 'tech' || 
                  user?.role === 'tech-admin' || 
                  user?.role === 'facilities' || 
                  user?.role === 'facilities-admin' ||
                  user?.department === 'technology' || 
                  user?.department === 'facilities') && user?.role !== 'custodian' && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-sm font-semibold text-gray-700 mb-4">Manage Ticket</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                        <select value={ticketDraft.status} onChange={(e) => setTicketDraft({ ...ticketDraft, status: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="open">Open</option>
                          <option value="in-progress">In Progress</option>
                          <option value="resolved">Resolved</option>
                        </select>
                      </div>
                      {(ticketDraft.status === 'resolved') && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Resolution <span className="text-red-500">*</span></label>
                          <textarea 
                            value={ticketDraft.resolution || ''} 
                            onChange={(e) => setTicketDraft({ ...ticketDraft, resolution: e.target.value })}
                            placeholder="Describe how the issue was resolved..."
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows={3}
                          />
                        </div>
                      )}
					  <div>
  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
  <select value={ticketDraft.department} onChange={(e) => setTicketDraft({ ...ticketDraft, department: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
    <option value="technology">Technology</option>
    <option value="facilities">Facilities</option>
  </select>
</div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                        <select value={ticketDraft.priority} onChange={(e) => setTicketDraft({ ...ticketDraft, priority: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Assign To</label>
                        <select value={ticketDraft.assigned_to} onChange={(e) => setTicketDraft({ ...ticketDraft, assigned_to: e.target.value })} className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                          <option value="">Unassigned</option>
                          {teamMembers.map(member => (
                            <option key={member.id} value={member.id}>{member.name}</option>
                          ))}
                        </select>
                      </div>
					  <div className="pt-4">
  <button 
    onClick={saveTicketChanges}
    disabled={loading}
    style={{backgroundColor: loading ? '#9ca3af' : PRIMARY_COLOR}}
    className="w-full px-4 py-2.5 text-white rounded-lg hover:opacity-90 transition font-medium disabled:opacity-50"
  >
    {loading ? 'Saving...' : 'Save Changes'}
  </button>
</div>
                      <div className="pt-4 mt-4 border-t border-gray-200 space-y-3">
                        <button 
                          onClick={handleOpenMergeModal}
                          className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
                        >
                          Merge Tickets
                        </button>
                        <button 
                          onClick={() => setShowDeleteConfirm(true)}
                          className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                        >
                          Delete Ticket
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Ticket?</h3>
                      <p className="text-gray-600 mb-6">Are you sure you want to delete this ticket? It will be moved to the deleted tickets area and can be restored later.</p>
                      <div className="flex gap-3">
                        <button 
                          onClick={() => deleteTicket(selectedTicket.id)}
                          className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium"
                        >
                          Yes, Delete
                        </button>
                        <button 
                          onClick={() => setShowDeleteConfirm(false)}
                          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Merge Tickets Modal */}
      {showMergeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseMergeModal}>
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full mx-4 max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Merge Tickets into Ticket #{selectedTicket?.id}</h3>
                <p className="text-sm text-gray-600 mt-1">Select tickets to merge. All comments and attachments will be moved to this ticket.</p>
              </div>
              <button onClick={handleCloseMergeModal} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Search bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input 
                  type="text" 
                  value={mergeSearchQuery} 
                  onChange={(e) => setMergeSearchQuery(e.target.value)} 
                  placeholder="Search tickets by title, location, or submitter..." 
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                />
              </div>
            </div>
            
            {/* Tickets list */}
            <div className="flex-1 overflow-y-auto border rounded-lg">
              {mergeLoading ? (
                <div className="text-center py-12 text-gray-500">Loading tickets...</div>
              ) : mergeableTickets.filter(ticket => {
                if (!mergeSearchQuery) return true;
                const query = mergeSearchQuery.toLowerCase();
                return ticket.title.toLowerCase().includes(query) ||
                       ticket.location.toLowerCase().includes(query) ||
                       (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(query));
              }).length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  {mergeSearchQuery ? 'No tickets match your search' : 'No mergeable tickets found'}
                </div>
              ) : (
                <div className="divide-y">
                  {mergeableTickets.filter(ticket => {
                    if (!mergeSearchQuery) return true;
                    const query = mergeSearchQuery.toLowerCase();
                    return ticket.title.toLowerCase().includes(query) ||
                           ticket.location.toLowerCase().includes(query) ||
                           (ticket.submitted_by_name && ticket.submitted_by_name.toLowerCase().includes(query));
                  }).map(ticket => (
                    <label 
                      key={ticket.id} 
                      className="flex items-start gap-4 p-4 hover:bg-gray-50 cursor-pointer transition"
                    >
                      <input 
                        type="checkbox" 
                        checked={selectedMergeTickets.includes(ticket.id)}
                        onChange={() => toggleMergeTicketSelection(ticket.id)}
                        className="mt-1.5 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-gray-900">#{ticket.id}</span>
                          <h4 className="font-semibold text-gray-900 truncate">{ticket.title}</h4>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ticket.status)}`}>
                            {ticket.status.toUpperCase()}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority.toUpperCase()}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3" />
                            {ticket.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {ticket.submitted_by_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {ticket.comment_count || 0} comments
                          </span>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer with action buttons */}
            <div className="mt-4 pt-4 border-t flex items-center justify-between">
              <div className="text-sm text-gray-600">
                {selectedMergeTickets.length > 0 ? (
                  <span className="font-medium">{selectedMergeTickets.length} ticket(s) selected</span>
                ) : (
                  <span>Select tickets to merge</span>
                )}
              </div>
              <div className="flex gap-3">
                <button 
                  onClick={handleCloseMergeModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleMergeTickets}
                  disabled={selectedMergeTickets.length === 0 || mergeLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mergeLoading ? 'Merging...' : `Merge ${selectedMergeTickets.length} Ticket(s)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Comments Popup Modal */}
      {showCommentsForTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleCloseComments}>
          <div className="bg-white rounded-xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Comments for: {showCommentsForTicket.title}</h3>
              <button onClick={handleCloseComments} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3">
              {cardCommentsLoading ? (
                <p className="text-gray-500 text-center py-8">Loading comments...</p>
              ) : cardComments.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No comments yet</p>
              ) : (
                cardComments.map(comment => (
                  <div key={comment.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="bg-gray-200 w-6 h-6 rounded-full flex items-center justify-center">
                        <User className="w-3 h-3 text-gray-600" />
                      </div>
                      <span className="font-medium text-gray-900 text-sm">{comment.author_name}</span>
                      <span className="text-xs text-gray-500">{new Date(comment.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-gray-700 text-sm ml-8">{comment.text}</p>
                  </div>
                ))
              )}
            </div>
            <div className="mt-4 pt-4 border-t">
              <button 
                onClick={() => { handleCloseComments(); openTicketDetail(showCommentsForTicket); }}
                style={{backgroundColor: PRIMARY_COLOR}}
                className="w-full px-4 py-2 text-white rounded-lg hover:opacity-90 transition font-medium"
              >
                View Full Ticket Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
